import { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Card, Empty, Input, Pagination, Segmented, Select, Space, Spin, Switch, Tag, Typography } from 'antd';
import { CloseOutlined, TeamOutlined, UserAddOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { Audience, AudiencePreview, ContactListItem, ResolvedEmails, RuleGroup } from '@crm/shared';
import { emptyRuleGroup } from '@crm/shared';
import { api, errorMessage } from '../../api/client';
import { FilterBuilder } from '../../components/FilterBuilder';
import { ContactSelect } from '../../components/ContactSelect';
import { useFields } from '../../features/fields/api';
import { useAudienceData, useLists, useSegments, useTags } from '../../features/audience/api';
import { contactName, useCreateContact } from '../../features/contacts/api';
import { useAudiencePeople, useResolveEmails, type VerifiedRule } from '../../features/campaigns/api';
import { plural } from '../../utils/format';
import { CardTitle } from '../../components/StatCard';
import { palette } from '../../app/theme';

export const SKIP_LABELS: Record<string, string> = { UNSUBSCRIBED: 'Unsubscribed', SUPPRESSED: 'Bounced or complained', NO_EMAIL: 'No email address', UNVERIFIED: 'Email not verified', INVALID: 'Invalid email', DUPLICATE_EMAIL: 'Same email as another recipient' };

interface Props {
  value: Audience;
  onChange: (a: Audience) => void;
  preview?: AudiencePreview;
  previewLoading?: boolean;
  previewError?: unknown;
  verified: VerifiedRule;
  onVerified: (v: Partial<VerifiedRule>) => void;
}

export function AudienceEditor({ value, onChange, preview, previewLoading, previewError, verified, onVerified }: Props) {
  const defs = useFields('CONTACT');
  const orgDefs = useFields('ORGANIZATION');
  const audience = useAudienceData();
  // same cached queries useAudienceData reads; used here only for their loading state
  const segLoading = useSegments().isLoading;
  const listLoading = useLists().isLoading;
  const tagLoading = useTags().isLoading;
  const inc = value.include ?? {};
  const exc = value.exclude ?? {};
  const setInc = (patch: Partial<NonNullable<Audience['include']>>) => onChange({ ...value, include: { ...inc, ...patch } });
  const setExc = (patch: Partial<NonNullable<Audience['exclude']>>) => onChange({ ...value, exclude: { ...exc, ...patch } });
  const opts = (xs: { id: string; name: string }[]) => xs.map((x) => ({ value: x.id, label: x.name }));

  return (
    <div className="recipients-layout">
      <Space orientation="vertical" style={{ width: '100%' }} size={24}>
        <Card title={<CardTitle title="Groups" subtitle="Everyone in these segments, lists or tags, or matching a filter" />}>
          <Space orientation="vertical" style={{ width: '100%' }} size="small">
            <Space><Switch aria-label="Everyone in the CRM" checked={!!value.allContacts} onChange={(c) => onChange({ ...value, allContacts: c })} /><Typography.Text>Everyone in the CRM</Typography.Text></Space>
            {!value.allContacts && (
              <>
                <div><Typography.Text type="secondary">Segments</Typography.Text><Select mode="multiple" aria-label="Include segments" loading={segLoading} optionFilterProp="label" style={{ width: '100%' }} value={inc.segmentIds ?? []} onChange={(v) => setInc({ segmentIds: v })} options={opts(audience.segments)} placeholder="Any contact in these segments" /></div>
                <div><Typography.Text type="secondary">Lists</Typography.Text><Select mode="multiple" aria-label="Include lists" loading={listLoading} optionFilterProp="label" style={{ width: '100%' }} value={inc.listIds ?? []} onChange={(v) => setInc({ listIds: v })} options={opts(audience.lists)} placeholder="Any contact in these lists" /></div>
                <div><Typography.Text type="secondary">Tags</Typography.Text><Select mode="multiple" aria-label="Include tags" loading={tagLoading} optionFilterProp="label" style={{ width: '100%' }} value={inc.tagIds ?? []} onChange={(v) => setInc({ tagIds: v })} options={opts(audience.tags)} placeholder="Any contact with these tags" /></div>
                <div>
                  <Typography.Text type="secondary">Or a filter</Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <FilterBuilder value={(inc.filter as RuleGroup | undefined) ?? emptyRuleGroup()} onChange={(g) => setInc({ filter: g.conditions.length ? g : undefined })} contactFields={defs.data ?? []} organizationFields={orgDefs.data ?? []} audience={audience} />
                  </div>
                </div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>A contact is included when it matches any of the above.</Typography.Text>
              </>
            )}
          </Space>
        </Card>
        <PeopleCard ids={inc.contactIds ?? []} onChange={(contactIds) => setInc({ contactIds })} />
        <Card title={<CardTitle title="Leave out" subtitle="Contacts to skip even if they were picked above" />}>
          <Space orientation="vertical" style={{ width: '100%' }} size="small">
            <div><Typography.Text type="secondary">Segments</Typography.Text><Select mode="multiple" aria-label="Exclude segments" loading={segLoading} optionFilterProp="label" style={{ width: '100%' }} value={exc.segmentIds ?? []} onChange={(v) => setExc({ segmentIds: v })} options={opts(audience.segments)} placeholder="None" /></div>
            <div><Typography.Text type="secondary">Lists</Typography.Text><Select mode="multiple" aria-label="Exclude lists" loading={listLoading} optionFilterProp="label" style={{ width: '100%' }} value={exc.listIds ?? []} onChange={(v) => setExc({ listIds: v })} options={opts(audience.lists)} placeholder="None" /></div>
            <div><Typography.Text type="secondary">Tags</Typography.Text><Select mode="multiple" aria-label="Exclude tags" loading={tagLoading} optionFilterProp="label" style={{ width: '100%' }} value={exc.tagIds ?? []} onChange={(v) => setExc({ tagIds: v })} options={opts(audience.tags)} placeholder="None" /></div>
          </Space>
        </Card>
      </Space>
      <RecipientsPanel audience={value} verified={verified} onVerified={onVerified} preview={preview} previewLoading={previewLoading} previewError={previewError} />
    </div>
  );
}

const SHOWN_PICKED = 100;

/** Hand-picked contacts: search one at a time, or paste a batch of email addresses. */
function PeopleCard({ ids, onChange }: { ids: string[]; onChange: (ids: string[]) => void }) {
  const { message } = App.useApp();
  const [pasted, setPasted] = useState('');
  const [result, setResult] = useState<ResolvedEmails | null>(null);
  const resolve = useResolveEmails();
  const createContact = useCreateContact();
  const [creating, setCreating] = useState(false);
  const shown = ids.slice(0, SHOWN_PICKED);
  const people = useQuery({
    queryKey: ['contacts-by-id', shown],
    queryFn: async () => (await api.get<{ items: ContactListItem[] }>('/contacts', { params: { ids: shown.join(','), pageSize: SHOWN_PICKED } })).data.items,
    enabled: shown.length > 0,
    placeholderData: (prev) => prev,
  });
  const byId = useMemo(() => new Map((people.data ?? []).map((c) => [c.id, c])), [people.data]);
  const addIds = (more: string[]) => onChange([...new Set([...ids, ...more])]);

  const addPasted = async () => {
    const emails = pasted.split(/[\s,;]+/).map((s) => s.replace(/^<|>$/g, '')).filter(Boolean);
    if (!emails.length) return;
    try {
      const r = await resolve.mutateAsync(emails);
      addIds(r.found.map((f) => f.id));
      setResult(r);
      setPasted('');
      if (r.found.length) message.success(`${plural(r.found.length, 'person', 'people')} added`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  const createMissing = async () => {
    if (!result) return;
    setCreating(true);
    const made: string[] = [];
    try {
      for (const email of result.missing) made.push((await createContact.mutateAsync({ email })).id);
      addIds(made);
      message.success(`${plural(made.length, 'new contact')} added to the CRM and to this campaign`);
      setResult({ ...result, missing: [] });
    } catch (e) {
      if (made.length) addIds(made);
      message.error(errorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card title={<CardTitle title="People" subtitle="Add individual contacts, or type in their email addresses" />}>
      <Space orientation="vertical" style={{ width: '100%' }} size="middle">
        <div className="full-width-select">
          <Typography.Text type="secondary" style={{ display: 'block' }}>Find a contact</Typography.Text>
          <ContactSelect value={null} onChange={(id) => id && addIds([id])} />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block' }}>Email addresses</Typography.Text>
          <Input.TextArea aria-label="Email addresses" autoSize={{ minRows: 2, maxRows: 6 }} value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder="ada@example.com, bob@example.com …" />
          <Button style={{ marginTop: 8 }} icon={<UserAddOutlined />} onClick={() => void addPasted()} loading={resolve.isPending} disabled={!pasted.trim()}>Add addresses</Button>
        </div>
        {result && (result.missing.length > 0 || result.invalid.length > 0) && (
          <Alert
            type="warning"
            showIcon
            closable
            onClose={() => setResult(null)}
            title={result.missing.length ? `${plural(result.missing.length, 'address', 'addresses')} not in the CRM yet` : 'Some entries are not email addresses'}
            description={
              <Space orientation="vertical" size={6}>
                {result.missing.length > 0 && <Typography.Text style={{ fontSize: 13 }}>{result.missing.slice(0, 10).join(', ')}{result.missing.length > 10 ? ` and ${result.missing.length - 10} more` : ''}</Typography.Text>}
                {result.invalid.length > 0 && <Typography.Text type="danger" style={{ fontSize: 13 }}>Not addresses: {result.invalid.slice(0, 10).join(', ')}</Typography.Text>}
                {result.missing.length > 0 && <Button size="small" type="primary" loading={creating} onClick={() => void createMissing()}>Add them to the CRM as new contacts</Button>}
              </Space>
            }
          />
        )}
        {ids.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Typography.Text strong>{plural(ids.length, 'person', 'people')} added by hand</Typography.Text>
              <Button size="small" type="link" danger onClick={() => onChange([])}>Remove all</Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {shown.map((id) => {
                const c = byId.get(id);
                return (
                  <Tag key={id} closable closeIcon={<CloseOutlined aria-label="Remove" />} onClose={() => onChange(ids.filter((x) => x !== id))} style={{ marginInlineEnd: 0, padding: '2px 8px' }}>
                    {c ? `${contactName(c)}${c.email && contactName(c) !== c.email ? ` · ${c.email}` : ''}` : people.isLoading ? '…' : 'Removed contact'}
                  </Tag>
                );
              })}
              {ids.length > SHOWN_PICKED && <Typography.Text type="secondary">and {ids.length - SHOWN_PICKED} more</Typography.Text>}
            </div>
          </div>
        )}
      </Space>
    </Card>
  );
}

const PAGE_SIZE = 25;

/** Who the campaign goes to and who is left off (with why), updating as the audience changes. */
function RecipientsPanel({ audience, verified, onVerified, preview, previewLoading, previewError }: { audience: Audience; verified: VerifiedRule; onVerified: (v: Partial<VerifiedRule>) => void; preview?: AudiencePreview; previewLoading?: boolean; previewError?: unknown }) {
  const [kind, setKind] = useState<'eligible' | 'skipped'>('eligible');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(q.trim()), 300);
    return () => window.clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [kind, search, audience, verified.requireVerified, verified.includeUnconfirmed]);
  const people = useAudiencePeople(audience, verified, { kind, q: search || undefined, page, pageSize: PAGE_SIZE });
  const skippedTotal = preview ? Object.values(preview.skipped).reduce((a, n) => a + (n ?? 0), 0) : 0;
  const eligible = preview?.eligible ?? 0;

  return (
    <div style={{ position: 'sticky', top: 16 }}>
      <Card styles={{ body: { padding: 0 } }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 20px', borderBottom: `1px solid ${palette.border}`, flexWrap: 'wrap' }}>
          <Space>
            <TeamOutlined style={{ color: palette.textSecondary }} />
            <Typography.Text strong style={{ fontSize: 16 }}>
              {previewError ? 'Could not count recipients' : `${plural(eligible, 'recipient')}`}
            </Typography.Text>
            {previewLoading && <Spin size="small" />}
          </Space>
          <Segmented
            size="small"
            value={kind}
            onChange={(v) => setKind(v as 'eligible' | 'skipped')}
            options={[
              { value: 'eligible', label: 'Sending to' },
              { value: 'skipped', label: `Left off (${skippedTotal})` },
            ]}
          />
        </div>
        <div style={{ padding: '12px 20px 0' }}>
          <Input.Search allowClear aria-label="Filter the list" placeholder="Filter the list…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div style={{ padding: '4px 20px 8px', minHeight: 120 }}>
          {previewError ? (
            <Typography.Text type="danger" style={{ display: 'block', padding: '16px 0' }}>{errorMessage(previewError)}</Typography.Text>
          ) : people.isError ? (
            <Typography.Text type="danger" style={{ display: 'block', padding: '16px 0' }}>Could not load the list: {errorMessage(people.error)}</Typography.Text>
          ) : people.data && people.data.items.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={search ? 'Nobody matches that filter.' : kind === 'eligible' ? 'Nobody yet. Pick groups, people or type in addresses.' : 'Nobody is left off.'} />
          ) : (
            <Spin spinning={people.isFetching && !people.data}>
              {(people.data?.items ?? []).map((p) => (
                <div key={p.id} className="person-row">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contactName(p)}</div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{[p.email ?? 'No email', p.organizationName].filter(Boolean).join(' · ')}</Typography.Text>
                  </div>
                  {p.reason && <Tag color="warning" style={{ marginInlineEnd: 0 }}>{SKIP_LABELS[p.reason] ?? p.reason}</Tag>}
                </div>
              ))}
            </Spin>
          )}
        </div>
        {people.data && people.data.total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 12 }}>
            <Pagination size="small" simple current={page} pageSize={PAGE_SIZE} total={people.data.total} onChange={setPage} />
          </div>
        )}
        <div style={{ padding: '12px 20px 16px', borderTop: `1px solid ${palette.border}` }}>
          <Space style={{ marginBottom: 8 }}><Switch size="small" aria-label="Only send to verified emails" checked={verified.requireVerified} onChange={(v) => onVerified({ requireVerified: v })} /><Typography.Text>Only send to verified emails</Typography.Text></Space>
          {verified.requireVerified && (
            <div style={{ margin: '0 0 8px 32px' }}>
              <Space><Switch size="small" aria-label="Also send to Unknown and Catch-all" checked={verified.includeUnconfirmed} onChange={(v) => onVerified({ includeUnconfirmed: v })} /><Typography.Text>Also send to Unknown and Catch-all</Typography.Text></Space>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '4px 0 0' }}>
                Delivered emails mark the address Valid; hard bounces mark it Invalid.
              </Typography.Paragraph>
            </div>
          )}
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
            Each person gets their own copy, and appears once even if they're in several groups. People who unsubscribed or bounced are left off automatically.
          </Typography.Paragraph>
        </div>
      </Card>
    </div>
  );
}
